import React, { useState, useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { FaFacebook, FaTwitter, FaLinkedin, FaInstagram } from "react-icons/fa";
import { FiMail, FiPhone, FiMapPin } from "react-icons/fi";
import Footer from "../components/Footer";
import Header from "../components/Header";
import Loader from "../components/Loader";

// Leaflet-related imports
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet"; // Import Leaflet for custom markers
import "leaflet/dist/leaflet.css";

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });

    const timer = setTimeout(() => setIsLoading(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  const handleInputChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    const { name, email, message } = formData;

    const subject = encodeURIComponent("Info Message");
    const body = encodeURIComponent(
      `Hello,\n\nMy name is ${name}.\nMy email is ${email}.\n\nThis is my message:\n${message}`,
    );

    const mailtoLink = `mailto:info@petriekstructures.com?subject=${subject}&body=${body}`;

    window.location.href = mailtoLink;
  };

  // Coordinates for the two office locations
  const officeLocations = [
    {
      id: 1,
      name: "Colorado Office",
      lat: 38.8926,
      lng: -104.797,
      address:
        "Colorado State, 7150 Silver Torch Ter, Colorado Springs, CO 80919 - United States",
    },
    {
      id: 2,
      name: "Utah Office",
      lat: 40.3906,
      lng: -111.8502,
      address: "Utah State, 380 S 430 E, LEHI, UT 84043 - United States",
    },
  ];

  return (
    <>
      <Header />

      {/* Logo Section */}
      <div className="flex justify-center py-4">
        <img src="/logo.png" alt="Company Logo" className="w-24 h-auto" />
      </div>

      {isLoading ? (
        <Loader />
      ) : (
        <>
          {/* Hero Section */}
          <section className="bg-gradient-to-r from-indigo-600 to-[#29313F] text-white py-16 text-center">
            <h2
              className="text-3xl sm:text-4xl font-semibold"
              data-aos="fade-up"
            >
              Get In Touch With Us
            </h2>
            <p
              className="text-lg sm:text-xl mt-4"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              Have any questions? We’re here to help! Contact us below.
            </p>
          </section>

          {/* Contact Details */}
          <section className="py-16 bg-gray-200 text-center">
            <h2
              className="text-3xl sm:text-4xl font-semibold text-gray-900"
              data-aos="fade-up"
            >
              Petriek Structures LLC
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 mt-8">
              {[
                {
                  icon: <FiMail />,
                  title: "Email",
                  details: "info@petriekstructures.com",
                  link: "mailto:info@petriekstructures.com",
                },
                {
                  icon: <FiPhone />,
                  title: "Phone",
                  details: ["(702) 972-2139", ""],
                  link: ["tel:(702) 972-2139", "8"],
                },
                {
                  icon: <FiMapPin />,
                  title: "Address",
                  details:
                    "Colorado State, 7150 Silver Torch Ter, Colorado Springs, CO 80919 - United States",
                  link: "https://www.google.com/maps/place/?q=place_id:ChIJJWBJ2PJRE4cRKt1-3G0hmVA",
                  linkText: "Our Office",
                },
                {
                  icon: <FiMapPin />,
                  title: "Address",
                  details:
                    "Utah State, 380 S 430 E, LEHI, UT 84043 - United States",
                  link: "https://www.google.com/maps/place/?q=place_id:ChIJE0yQEsWBTYcRTVjuQD-1W-8",
                  linkText: "Our Office",
                },
              ].map((item, index) => (
                <div
                  key={index}
                  className="bg-white shadow-lg p-6 rounded-lg hover:shadow-2xl transition-all duration-300"
                  data-aos="fade-up"
                  data-aos-delay={600 + index * 200}
                >
                  <h3 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center justify-center">
                    <span className="mr-2 text-[#29313F]">{item.icon}</span>{" "}
                    {item.title}
                  </h3>
                  {Array.isArray(item.details) ? (
                    item.details.map((line, i) => (
                      <p key={i} className="text-gray-700">
                        <a href={item.link[i]} className="hover:underline">
                          {line}
                        </a>
                      </p>
                    ))
                  ) : (
                    <p className="text-gray-700">
                      {item.link ? (
                        <a href={item.link} className="hover:underline">
                          {item.details}
                        </a>
                      ) : (
                        item.details
                      )}
                    </p>
                  )}
                  <p className="text-xl font bold text-blue-900">
                    {item.link ? (
                      <a href={item.link} className="hover:underline">
                        {item.linkText}
                      </a>
                    ) : null}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* OpenStreetMap Section */}
          <section className="py-16 bg-gray-100">
            <h2
              className="text-3xl sm:text-4xl font-semibold text-center text-gray-900"
              data-aos="fade-up"
            >
              Our Office Locations
            </h2>
            <div className="w-full h-96 mt-8">
              <MapContainer
                center={[38.8926, -104.797]}
                zoom={5}
                style={{ height: "100%", width: "100%" }}
              >
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                {officeLocations.map((office) => (
                  <Marker
                    key={office.id}
                    position={[office.lat, office.lng]}
                    icon={
                      new L.Icon({
                        iconUrl: require("leaflet/dist/images/marker-icon.png"),
                        iconSize: [25, 41],
                        iconAnchor: [12, 41],
                      })
                    }
                  >
                    <Popup>
                      {office.name} <br /> {office.address}
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          </section>

          {/* Contact Form */}
          <section className="py-16 bg-gray-50 text-center">
            <h2
              className="text-3xl sm:text-4xl font-semibold text-gray-900"
              data-aos="fade-up"
            >
              Send Us A Message
            </h2>
            <form
              onSubmit={handleSubmit}
              className="max-w-lg mx-auto bg-white p-8 mt-8 rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300"
            >
              {[
                { name: "name", type: "text", placeholder: "Your Name" },
                { name: "email", type: "email", placeholder: "Your Email" },
              ].map((input, index) => (
                <div key={index} className="mb-6">
                  <input
                    type={input.type}
                    name={input.name}
                    value={formData[input.name]}
                    onChange={handleInputChange}
                    placeholder={input.placeholder}
                    className="w-full p-4 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#29313F]"
                    required
                  />
                </div>
              ))}
              <div className="mb-6">
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Your Message"
                  rows="5"
                  className="w-full p-4 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#29313F]"
                  required
                ></textarea>
              </div>
              <button
                type="submit"
                className="px-6 py-3 bg-[#29313F] text-white font-semibold rounded-lg hover:bg-[#1f2633] hover:scale-105 transition-transform"
              >
                Send Message
              </button>
            </form>
          </section>

          {/* Social Media Links */}
          <section className="py-16 bg-[#29313F] text-white text-center">
            <h2
              className="text-3xl sm:text-4xl font-semibold"
              data-aos="fade-up"
            >
              Connect With Us
            </h2>
            <div className="flex justify-center space-x-6 mt-6">
              {[
                { icon: <FaFacebook />, link: "https://facebook.com" },
                { icon: <FaTwitter />, link: "https://twitter.com" },
                { icon: <FaLinkedin />, link: "https://linkedin.com" },
                { icon: <FaInstagram />, link: "https://instagram.com" },
              ].map((social, index) => (
                <a
                  key={index}
                  href={social.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.link}
                  className="text-4xl transition-transform hover:scale-110"
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </section>

          <Footer />
        </>
      )}
    </>
  );
};

export default ContactUs;
